import { Buffer } from 'buffer'
import type { Point } from './xrTeleportGeometry'

export const teleportLimits = { maxDistance: 6, cooldownMs: 900, maxRise: 1.25, maxDrop: 3 }
export type TeleportLimits = typeof teleportLimits
export interface TeleportTransport {
  state: string
  write: (name: any, data: any) => void
  on: (event: any, listener: (...args: any[]) => void) => any
  removeListener: (event: any, listener: (...args: any[]) => void) => any
}

/** This class can only send requests. It has no reference to the player or movement API. */
export class XRTeleportClient {
  capabilities: TeleportLimits | null = null
  status = 'Teleport unavailable · choose Walk in Options'
  aiming = false
  pendingId: number | null = null
  private nextId = 1
  private pressed = false
  private armed = false
  private sentAt = -Infinity
  private blinkAt = -Infinity
  private helloSent = false
  private retried = false
  private closed = false

  constructor(private transport: TeleportTransport, private now = () => performance.now()) {
    transport.on('state', this.onState)
    transport.on('custom_payload', this.onPayload)
    this.onState(transport.state)
  }

  private send(message: object) {
    this.transport.write('custom_payload', { channel: 'jerpg:vr', data: Buffer.from(JSON.stringify(message), 'utf8') })
  }

  private onState = (state: string) => {
    if (this.closed) return
    if (state !== 'play') {
      this.capabilities = null; this.helloSent = false; this.retried = false
      this.cancel(); this.pendingId = null; this.blinkAt = -Infinity
      this.status = 'Teleport unavailable · choose Walk in Options'
      return
    }
    if (this.helloSent) return
    this.helloSent = true
    // registerChannel's second argument is a ProtoDef parser, not raw JSON.
    this.transport.write('custom_payload', { channel: 'minecraft:register', data: Buffer.from('jerpg:vr', 'utf8') })
    this.send({ v: 1, type: 'hello' })
  }

  worldReady() {
    if (!this.closed && this.transport.state === 'play' && this.helloSent && !this.retried) {
      this.retried = true
      this.send({ v: 1, type: 'hello' })
    }
  }

  private onPayload = (packet: { channel: string; data: Uint8Array }) => {
    if (this.closed || this.transport.state !== 'play' || packet.channel !== 'jerpg:vr' || !packet.data || packet.data.byteLength > 512) return
    let data: any
    try { data = JSON.parse(Buffer.from(packet.data).toString('utf8')) } catch { return }
    if (!data || data.v !== 1) return
    if (data.type === 'capabilities') {
      if (data.teleport === false) {
        this.capabilities = null; this.cancel()
        this.status = 'Teleport unavailable · choose Walk in Options'
        return
      }
      if (data.teleport !== true || !Object.keys(teleportLimits).every(key => typeof data[key] === 'number' && Number.isFinite(data[key]) && data[key] > 0)) return
      this.capabilities = {
        maxDistance: Math.min(6, data.maxDistance), maxRise: Math.min(1.25, data.maxRise), maxDrop: Math.min(3, data.maxDrop),
        cooldownMs: Math.min(60000, Math.max(900, data.cooldownMs)),
      }
      this.status = 'Teleport ready · hold left trigger to aim'
    }
    if (data.type === 'result' && Number.isSafeInteger(data.id) && data.id === this.pendingId && typeof data.accepted === 'boolean' && typeof data.reason === 'string') {
      this.pendingId = null
      this.status = data.accepted ? 'Teleport accepted' : `Teleport refused: ${data.reason.slice(0, 100)}`
    }
  }

  cancel() { this.aiming = false; this.armed = false; this.pressed = false }

  update(pressed: boolean, allowed: boolean, target: Point | null, time = this.now()) {
    if (this.pendingId !== null && time - this.sentAt >= 1800) {
      this.pendingId = null
      this.status = 'Teleport timed out · try again'
    }
    if (this.closed || !allowed || !this.capabilities || this.transport.state !== 'play') { this.cancel(); return }
    if (!pressed && !this.pressed) this.armed = true
    const canRequest = this.pendingId === null && time - this.sentAt >= this.capabilities.cooldownMs
    if (pressed && !this.pressed && this.armed && canRequest) this.aiming = true
    if (!pressed && this.pressed) {
      const request = this.aiming && canRequest && target && [target.x, target.y, target.z].every(Number.isFinite)
      this.aiming = false; this.armed = true
      if (request && this.nextId < Number.MAX_SAFE_INTEGER) {
        this.pendingId = this.nextId++
        this.sentAt = time; this.blinkAt = time
        this.status = 'Teleport requested…'
        try { this.send({ v: 1, type: 'teleport', id: this.pendingId, x: target.x, y: target.y, z: target.z }) } catch {
          this.pendingId = null; this.blinkAt = -Infinity; this.status = 'Teleport connection unavailable'
        }
      }
    }
    this.pressed = pressed
  }

  /** Short bounded blink; even a lost reply can never leave the headset black. */
  blinkOpacity(time: number) {
    const elapsed = time - this.blinkAt
    return Math.max(0, Math.min(0.9, elapsed / 60, (350 - elapsed) / 150))
  }

  resetInput() { this.cancel(); this.blinkAt = -Infinity }

  dispose() {
    this.closed = true; this.resetInput(); this.pendingId = null; this.capabilities = null
    this.transport.removeListener('state', this.onState)
    this.transport.removeListener('custom_payload', this.onPayload)
  }
}
